import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Days, ScheduleType, SelectedDayType } from '../schedule-row/schedule-row.component';
import {
  ScheduleType as ScheduleTypeRequest,
  ScheduleControlService,
} from 'src/app/settings/services/schedule-control.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import constants from '../../constants';
import { Reminders } from 'src/app/partitions/models/partition';
import { ScheduleControlErrorsService } from 'src/app/settings/services/schedule-control.errors.service';
import { ConfirmActionDialogComponent } from '../confirm-action-dialog/confirm-action-dialog.component';

interface ScheduleEventsDialogData {
  partitionId: string;
  reminders: Reminders;
}

enum Errors {
  AtLeastOneRequiredDays = 'AtLeastOneRequiredDays',
  AtLeastOneRequired = 'AtLeastOneRequired',
  CanNotBeEqual = 'CanNotBeEqual',
}

enum DaysConverted {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
  Holidays = 7,
}

@Component({
  selector: 'app-schedule-events-dialog',
  templateUrl: './schedule-events-dialog.component.html',
  styleUrls: ['./schedule-events-dialog.component.scss'],
})
export class ScheduleEventsDialogComponent {
  partitionId: string;
  notifyMonitoringCenter: boolean = false;
  isLoading: boolean = false;
  checkInvalid: { scheduleId: number; fields: { [key: string]: string[] } } | undefined;
  schedules: ScheduleType[];
  daysAlreadySelected = {
    [Days.Monday]: false,
    [Days.Tuesday]: false,
    [Days.Wednesday]: false,
    [Days.Thursday]: false,
    [Days.Friday]: false,
    [Days.Saturday]: false,
    [Days.Sunday]: false,
    [Days.Holidays]: false,
  };

  constructor(
    public dialogRef: MatDialogRef<ScheduleEventsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ScheduleEventsDialogData,
    private readonly scheduleControlService: ScheduleControlService,
    private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService,
    private readonly scheduleControlErrorsService: ScheduleControlErrorsService,
    private readonly dialog: MatDialog
  ) {
    /** Clear errors on open is fundamental for the component to work correctly */
    this.scheduleControlErrorsService.clearErrors();

    this.partitionId = this.data.partitionId;

    this.setupIncomingReminders();
    this.fillDaysAlreadySelected();

    dialogRef.disableClose = true;
    dialogRef.backdropClick().subscribe(() => this.onCancelClick());
  }

  emptySchedule() {
    return [
      {
        id: 0,
        arm1: null,
        disarm1: null,
        arm2: null,
        disarm2: null,
        selectedDay: {
          [Days.Monday]: false,
          [Days.Tuesday]: false,
          [Days.Wednesday]: false,
          [Days.Thursday]: false,
          [Days.Friday]: false,
          [Days.Saturday]: false,
          [Days.Sunday]: false,
          [Days.Holidays]: false,
        },
      },
    ];
  }

  setupIncomingReminders() {
    if (this.data.reminders) {
      this.notifyMonitoringCenter = this.data.reminders.notifyMonitoringCenter;

      if (this.data.reminders.schedules && this.data.reminders.schedules.length > 0) {
        this.schedules = this.mapSchedulesFromReminders(this.data.reminders.schedules);
      } else {
        this.schedules = this.emptySchedule();
      }
    }
  }

  mapSchedulesFromReminders(schedules: ScheduleTypeRequest[]) {
    return schedules.map((schedule, index) => {
      return {
        id: index,
        arm1: schedule.arm1?.time,
        disarm1: schedule.disarm1?.time,
        arm2: schedule.arm2?.time,
        disarm2: schedule.disarm2?.time,
        selectedDay: {
          [Days.Monday]: schedule.days.includes(DaysConverted.Monday),
          [Days.Tuesday]: schedule.days.includes(DaysConverted.Tuesday),
          [Days.Wednesday]: schedule.days.includes(DaysConverted.Wednesday),
          [Days.Thursday]: schedule.days.includes(DaysConverted.Thursday),
          [Days.Friday]: schedule.days.includes(DaysConverted.Friday),
          [Days.Saturday]: schedule.days.includes(DaysConverted.Saturday),
          [Days.Sunday]: schedule.days.includes(DaysConverted.Sunday),
          [Days.Holidays]: schedule.days.includes(DaysConverted.Holidays),
        },
      };
    });
  }

  fillDaysAlreadySelected() {
    this.schedules.forEach((schedule) => {
      Object.keys(schedule.selectedDay).forEach((day: string) => {
        if (schedule.selectedDay[day]) {
          this.daysAlreadySelected[day] = true;
        }
      });
    });
  }

  onSendEventToCentralChange(event: boolean) {
    this.notifyMonitoringCenter = event;
  }

  openConfirmDialog(promptText: string, submitText: string, callback: Function): void {
    const dialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      maxWidth: '450px',
      autoFocus: false,
      data: { promptText, submitText },
    });

    dialogRef.afterClosed().subscribe((result) => callback(result));
  }

  onCancelClick() {
    this.openConfirmDialog(
      this.translateService.instant('Partition.ExitForm'),
      this.translateService.instant('Shared.Leave'),
      (result: boolean) => {
        if (result) {
          this.dialogRef.close(true);
        }
      }
    );
  };

  async onNewScheduleButtonClick() {
    if (this.schedules.length === 4) return;

    this.scheduleControlErrorsService.clearErrors();
    this.schedules.push({
      id: this.schedules.length,
      arm1: null,
      disarm1: null,
      arm2: null,
      disarm2: null,
      selectedDay: {
        [Days.Monday]: false,
        [Days.Tuesday]: false,
        [Days.Wednesday]: false,
        [Days.Thursday]: false,
        [Days.Friday]: false,
        [Days.Saturday]: false,
        [Days.Sunday]: false,
        [Days.Holidays]: false,
      },
    });

    this.remakeIds();
  }

  remakeIds() {
    this.schedules = this.schedules.map((schedule, index) => {
      schedule.id = index;
      return schedule;
    });
  }

  onDeleteButtonClick(scheduleIncoming: ScheduleType) {
    const daysMustRemoveFromAlreadySelect = scheduleIncoming.selectedDay;

    if (this.schedules.length === 1) {
      this.scheduleControlErrorsService.clearErrors();
      this.resetSchedulesForm();
    } else {
      this.schedules = this.schedules.filter((schedule) => schedule.id !== scheduleIncoming.id);
    }

    Object.keys(daysMustRemoveFromAlreadySelect).forEach((day: string) => {
      if (daysMustRemoveFromAlreadySelect[day]) {
        this.daysAlreadySelected[day] = false;
      }
    });

    this.remakeIds();

    this.onSaveClick(true);
  }

  resetSchedulesForm() {
    this.notifyMonitoringCenter = false;
    this.schedules = [
      {
        id: 0,
        arm1: null,
        disarm1: null,
        arm2: null,
        disarm2: null,
        selectedDay: {
          [Days.Monday]: false,
          [Days.Tuesday]: false,
          [Days.Wednesday]: false,
          [Days.Thursday]: false,
          [Days.Friday]: false,
          [Days.Saturday]: false,
          [Days.Sunday]: false,
          [Days.Holidays]: false,
        },
      },
    ];
  }

  /**
   * The schedule that owns the slot can open it otherwise no one can
   */
  openSlot(day: Days, scheduleIncomingId: number) {
    this.daysAlreadySelected[day] = false;

    if (this.schedules[scheduleIncomingId].id === scheduleIncomingId) {
      this.schedules[scheduleIncomingId].selectedDay[day] = false;
      this.schedules[scheduleIncomingId].freeSlot = undefined;
    }
  }

  onScheduleChange(scheduleIncoming: ScheduleType) {
    /**
     * If the schedule has a free slot, then we can open it for others to claim it
     */
    if (scheduleIncoming.freeSlot) {
      this.openSlot(scheduleIncoming.freeSlot.day, scheduleIncoming.id);
      return;
    }

    if (this.schedules[scheduleIncoming.id].id === scheduleIncoming.id) {
      this.schedules[scheduleIncoming.id] = scheduleIncoming;
    }

    this.fillDaysAlreadySelected();
    this.remakeIds();
  }

  /**
   * Find the variables that are equal
   * To validate that the user is not setting the same hour for arm and disarm
   * To make the labels return unique for each input we need to change the labels to:
   * arm1, disarm1, arm2, disarm2. But this will complicate the validation logic.
   * @param arm1
   * @param disarm1
   * @param arm2
   * @param disarm2
   * @returns which labels are equal to each other but reduces to just arm, disarm
   */
  findEqualVariables(arm1: string, disarm1: string, arm2: string, disarm2: string): string[] {
    const variables = [];

    if (arm1 !== null && disarm1 !== null && arm2 === null && disarm2 === null) {
      variables.push(
        { label: 'arm1', value: arm1 },
        { label: 'disarm1', value: disarm1 }
      );
    } else if (arm1 === null && disarm1 === null && arm2 !== null && disarm2 !== null) {
      variables.push(
        { label: 'arm2', value: arm2 },
        { label: 'disarm2', value: disarm2 }
      );
    } else {
      variables.push(
        { label: 'arm1', value: arm1 },
        { label: 'disarm1', value: disarm1 },
        { label: 'arm2', value: arm2 },
        { label: 'disarm2', value: disarm2 }
      );
    }

    const valueGroups = new Map<string, string[]>();

    variables.forEach((variable) => {
      if (variable.value !== null) {
        if (!valueGroups.has(variable.value)) {
          valueGroups.set(variable.value, []);
        }
        valueGroups.get(variable.value)?.push(variable.label);
      }
    });

    let equalLabels: string[] = [];
    valueGroups.forEach((labels) => {
      if (labels.length > 1) {
        equalLabels.push(...labels);
      }
    });

    equalLabels.sort();

    const filteredLabels = equalLabels.filter((label) => label.includes('arm') || label.includes('disarm'));

    return filteredLabels;
  }

  isInvalid() {
    let problematicHourMinuteField = {
      arm: [],
      disarm: [],
      days: [],
    };

    for (const schedule of this.schedules) {
      if (schedule.arm1 === '') schedule.arm1 = null;
      if (schedule.disarm1 === '') schedule.disarm1 = null;
      if (schedule.arm2 === '') schedule.arm2 = null;
      if (schedule.disarm2 === '') schedule.disarm2 = null;

      const armDisarm1 = schedule.arm1 === null && schedule.disarm1 === null;
      const armDisarm2 = schedule.arm2 === null && schedule.disarm2 === null;

      const allNull = armDisarm1 && armDisarm2;

      if (allNull) {
        problematicHourMinuteField['arm'].push(Errors.AtLeastOneRequired);
        problematicHourMinuteField['disarm'].push(Errors.AtLeastOneRequired);
      }

      const equals = this.findEqualVariables(schedule.arm1, schedule.disarm1, schedule.arm2, schedule.disarm2);

      if (equals.length > 0) {
        equals.forEach((equal) => {
          if (equal.startsWith('arm')) {
            problematicHourMinuteField['arm'].push(Errors.CanNotBeEqual);
          }
          if (equal.startsWith('disarm')) {
            problematicHourMinuteField['disarm'].push(Errors.CanNotBeEqual);
          }
        });
      }

      if (this.toDays(schedule.selectedDay).length === 0) {
        problematicHourMinuteField['days'].push(Errors.AtLeastOneRequiredDays);
      }

      if (
        Object.keys(problematicHourMinuteField.arm).length > 0 ||
        Object.keys(problematicHourMinuteField.disarm).length > 0 ||
        Object.keys(problematicHourMinuteField.days).length > 0
      ) {
        this.checkInvalid = {
          scheduleId: schedule.id,
          fields: problematicHourMinuteField,
        };

        return this.checkInvalid;
      }
    }

    return undefined;
  }

  prepareSchedules() {
    return this.schedules.map((schedule) => {
      return {
        arm1: {
          time: schedule.arm1 === '' ? null : schedule.arm1,
        },
        arm2: {
          time: schedule.arm2 === '' ? null : schedule.arm2,
        },
        disarm1: {
          time: schedule.disarm1 === '' ? null : schedule.disarm1,
        },
        disarm2: {
          time: schedule.disarm2 === '' ? null : schedule.disarm2,
        },
        days: this.toDays(schedule.selectedDay),
      };
    });
  }

  toDays(selectedDay: SelectedDayType) {
    return Object.keys(selectedDay).reduce((acc, day) => {
      if (selectedDay[day]) {
        acc.push(DaysConverted[day]);
      }
      return acc;
    }, []);
  }

  checkEmptySchedules() {
    return this.schedules.every(
      (schedule) =>
        schedule.arm1 === null &&
        schedule.disarm1 === null &&
        schedule.arm2 === null &&
        schedule.disarm2 === null &&
        Object.keys(schedule.selectedDay).every((day) => !schedule.selectedDay[day])
    );
  }

  checkUserOnlySelectedDaysNotFilledHours() {
    return this.schedules.every(
      (schedule) =>
        schedule.arm1 === null &&
        schedule.disarm1 === null &&
        schedule.arm2 === null &&
        schedule.disarm2 === null &&
        Object.keys(schedule.selectedDay).some((day) => schedule.selectedDay[day])
    );
  }

  onSaveClick(dontLeave?: boolean) {
    this.checkInvalid = this.isInvalid();
    const areSchedulesResetFullyByUser = this.checkEmptySchedules();
    const userOnlySelectedDaysNotFilledHours = this.checkUserOnlySelectedDaysNotFilledHours();

    if (
      !areSchedulesResetFullyByUser &&
      this.checkInvalid != undefined &&
      Object.keys(this.checkInvalid.fields).length > 0
    ) {
      this.snackBar.open(
        this.translateService.instant('Partition.ArmedDisarmedEventsError'),
        this.translateService.instant('Shared.Close'),
        { duration: constants.snackBarErrorDuration }
      );
      this.scheduleControlErrorsService.setErrors(this.checkInvalid);
      return;
    }

    if (userOnlySelectedDaysNotFilledHours) {
      this.snackBar.open(
        this.translateService.instant('Partition.NeedArmDisarmHourMinuteToBeFilled'),
        this.translateService.instant('Shared.Close'),
        { duration: constants.snackBarErrorDuration }
      );
      return;
    }

    const parsedSchedules = this.prepareSchedules();

    this.isLoading = true;
    this.scheduleControlErrorsService.clearErrors();

    this.scheduleControlService
      .upsertReminders({
        partitionId: this.partitionId,
        notifyMonitoringCenter: this.notifyMonitoringCenter,
        schedules: areSchedulesResetFullyByUser ? [] : parsedSchedules,
      })
      .subscribe({
        next: () => {
          this.isLoading = false;
          if (!dontLeave) {
            this.dialogRef.close(true);
          }
          this.snackBar.open(
            this.translateService.instant('Shared.Success'),
            this.translateService.instant('Shared.Close'),
            { duration: constants.snackBarDuration }
          );
        },
        error: () => {
          this.isLoading = false;
          this.snackBar.open(
            this.translateService.instant('Shared.SomethingWentWrong'),
            this.translateService.instant('Shared.Close'),
            { duration: constants.snackBarErrorDuration }
          );
        },
      });
  }
}
